import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { APP_CONFIG } from "../lib/angular/injection-tokens";
import { IAppConfig } from "../lib/config/app-config";
import { LearnerCourseLog } from "../_models/LearnerCourseLog";
import {
  LearnerLearningObject,
  LearnerLearningRecord,
} from "../_models/LearnerLearningRecord";
import { ScormEntry } from "../_models/ScormEntry";
import { ScormSession } from "../_models/ScormSession";
import { SkillsCheckContent } from "../_models/SkillsCheck";
import { AppConstants } from "../constants/constants";

@Injectable({
  providedIn: "root",
})
export class LearningTrackingService {
  private baseUrl: string = this.config.protectedResourceEndpoint;
  private static readonly path: string = "learningtracking/";

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) private config: IAppConfig
  ) {}

  public getLearningRecordByCourseId$(
    courseId: number
  ): Observable<LearnerLearningRecord[]> {
    return this.http.get<LearnerLearningRecord[]>(
      this.baseUrl + LearningTrackingService.path + "learningrecord/" + courseId
    );
  }

  public getLearningRecordByLearnerId$(
    learnerId: number, courseId: number
  ): Observable<LearnerLearningRecord[]> {
    let params: HttpParams = new HttpParams();
    params = params.append(AppConstants.LEARNER_ID, String(learnerId));
    params = params.append(AppConstants.COURSE_ID, String(courseId));
    return this.http.get<LearnerLearningRecord[]>(
      this.baseUrl + LearningTrackingService.path + "learner/learningrecord/",
      {params}
    );
  }

  public updateLearningObjectDateVisitedByObjectId$(
    objectId: string
  ): Observable<LearnerLearningObject> {
    return this.http.get<LearnerLearningObject>(
      this.baseUrl +
        LearningTrackingService.path +
        "updatelearningobjectdatevisited/" +
        objectId
    );
  }

  public updateLearnerCourseLogDateByCourseId$(
    courseId: number
  ): Observable<LearnerCourseLog> {
    return this.http.get<LearnerCourseLog>(
      this.baseUrl +
        LearningTrackingService.path +
        "updatelearnercourselogdate/" +
        courseId
    );
  }

  public assignContentForSkillsCheck$(
    content: SkillsCheckContent
  ): Observable<SkillsCheckContent> {
    return this.http.post<SkillsCheckContent>(
      this.baseUrl + LearningTrackingService.path + "assigncontentskillcheck",
      content
    );
  }

  public saveSessionStateLesson$(
    session: ScormSession
  ): Observable<ScormEntry[]> {
    session.scormEntries.forEach((entry) => {
      entry.scormSessionId = session.id;
      entry.learnerCourseLogId = session.learnerCourseLogId;
    });

    if (!session.open) {
      return null;
    }

    return this.http.put<ScormEntry[]>(
      this.baseUrl +
        LearningTrackingService.path +
        "updatebulkdatalesson",
      session.scormEntries
    );
  }

  public updateBulkDateStarted$(learnerLearningObjectIds: number[]) : Observable<boolean>{
    return this.http.put<boolean>(
      this.baseUrl +
      LearningTrackingService.path + 
      "updatelearningobjectdatestarted",
      learnerLearningObjectIds
    );
  }

  public saveSessionStateSkillCheck$(
    session: ScormSession,
    courseId: number
  ): Observable<ScormEntry[]> {
    session.scormEntries.forEach((entry) => {
      entry.scormSessionId = session.id;
      entry.learnerCourseLogId = session.learnerCourseLogId;
    });

    if (!session.open) {
      return null;
    }

    return this.http.put<ScormEntry[]>(
      this.baseUrl +
        LearningTrackingService.path +
        "updatebulkdataskillcheck/"+courseId,
      session.scormEntries
    );
  }

  public getIsEnableLearningTab$(courseId: number): Observable<boolean> {
    return this.http.get<boolean>(
      this.baseUrl +
        LearningTrackingService.path +
        "getisenablelearningtab/" +
        courseId
    );
  }

  public getIsEnableLearningTabByLearnerId$(learnerId: number, courseId: number): Observable<boolean> {
    let params: HttpParams = new HttpParams();
    params = params.append(AppConstants.LEARNER_ID, String(learnerId));
    params = params.append(AppConstants.COURSE_ID, String(courseId));
    return this.http.get<boolean>(
      this.baseUrl +
        LearningTrackingService.path +
        "learner/getisenablelearningtab",
        {params}
    );
  }



  public createSessionByObjectIdSkillCheck$(objectId: string): Observable<ScormSession> {
    return this.http.get<ScormSession>(this.baseUrl + LearningTrackingService.path + "usersessionskillcheck/" + objectId);
  }
}
